import React, { useState, useEffect, useContext } from "react";
import { Table, TableRow, TableBody, TableHead } from "@material-ui/core";
import { useQuery } from "@apollo/client";

import StyledTableCell, {
  HeadRow,
} from "../../common/styled-table-cell.component";
import {
  getResponseNextLevelData,
  prepareTableRowsData,
} from "../../../utils/create-table-data";
import StockpileHeader from "../../common/stockpile-header.component";
import DataCell from "../../common/data-cell.component";
import {
  APP_INSIGHTS_EVENTS,
  MATCHING_ERRORS_MESSAGES,
  STOCKPILES_TABLE_COLUMNS_NUMBER,
  STOCKPILES_TABLE_FEATURES,
} from "../../../constants";
import {
  GET_EQUIPMENT_AVAILABILITY,
  GET_STOCKPILES_INPUT,
} from "../../../queries/stockpiles.gql";
import { MatchContext } from "../../../context";
import {
  SET_DATA,
  SET_FORECASTED_STOCKPILE,
  SET_HIGHLIGHTED_ROWS,
} from "../../../reduce/actionTypes";
import IntraTableMessage from "../../HOC/intratable-message/intratable-message.component";
import LoaderBar from "../../common/loader-bar.component";
import ErrorMessage from "../../common/error-message.component";
import useStyles from "../../tables-container/styles";
import useHighlightTableItem from "../../../utils/hooks/use-higlight-table-item";
import prepareRowStyles from "../../../utils/prepare-row-styles";
import { prepareStockpiles } from "../../../utils/prepare-stockpiles-for-equipment-avl";
import usePollStockpiles from "../../../utils/hooks/use-poll-stockpiles";

const FilledTable = ({ stockpilesTableFeatures }): JSX.Element => {
  const classes = useStyles();
  const { state, dispatch } = useContext(MatchContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredStockpile, setHoveredStockpile] = useState("");
  const [hoveredParam, setHoveredParam] = useState("");
  const higlightItem = useHighlightTableItem();

  const {
    data,
    loading: loadingEq,
    error,
    refetch,
  } = useQuery(GET_EQUIPMENT_AVAILABILITY, {
    variables: {
      stockpilesData: state.stockpiles,
    },
  });

  const {
    data: stockpilesData,
    loading: stockLoading,
    refetch: refetchStockpiles,
  } = useQuery(GET_STOCKPILES_INPUT, {
    variables: {
      requestId: state.requestId,
    },
  });

  const { rows: stockpilePoll, error: errorPollStock } = usePollStockpiles(
    GET_STOCKPILES_INPUT,
    STOCKPILES_TABLE_FEATURES,
    "getInputData"
  );

  useEffect(() => {
    if (
      stockpilePoll.length &&
      stockpilePoll[0].stockpiles &&
      stockpilePoll[0].stockpiles.length
    ) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [stockpilePoll]);

  useEffect(() => {
    refetchStockpiles()
      .then(async (r) => {
        if (r.data) {
          const tableData = await getResponseNextLevelData(
            r.data["getInputData"]
          )[0];
          const rowsInputData = prepareTableRowsData(
            tableData,
            STOCKPILES_TABLE_FEATURES
          );

          if (rowsInputData[0].stockpiles.length) {
            const stockpiles = prepareStockpiles(rowsInputData[0].stockpiles);
            dispatch({
              type: SET_DATA,
              payload: { stockpiles },
            });
          }
        } else {
          dispatch({
            type: SET_DATA,
            payload: { loadingStockpilesData: true },
          });
        }
      })
      .then(() => refetch());
  }, [
    state.tableWeights,
    state.tableInputsPrevious,
    state.tableWeightsPrevious,
  ]);

  useEffect(() => {
    if (data) {
      let rowsData = prepareTableRowsData(
        data.getEquipmentAvailability,
        stockpilesTableFeatures
      );

      setRows(rowsData);
    } else {
      dispatch({
        type: SET_DATA,
        payload: { loadingStockpilesData: true },
      });
    }
  }, [data]);

  const handleStockpileDoubleClick = (item: string) => {
    const value = item;
    if (state.stockpileName) {
      dispatch({
        type: SET_FORECASTED_STOCKPILE,
        payload: value,
      });
    }
  };

  return (
    <React.Fragment>
      {error ? (
        <ErrorMessage error={error} />
      ) : loading ? (
        <IntraTableMessage>
          <LoaderBar />
        </IntraTableMessage>
      ) : rows[0] && !rows[0].stockpiles ? (
        <ErrorMessage
          error={error}
          customMessage={MATCHING_ERRORS_MESSAGES.MATCHING_ALGORYTHM}
        />
      ) : (
        <Table aria-label="customized table">
          <TableHead>
            <HeadRow>
              <StyledTableCell className={classes.paramCell} align="center">
                Utilization, %
              </StyledTableCell>
              <StockpileHeader
                data={rows}
                columnsNumber={STOCKPILES_TABLE_COLUMNS_NUMBER}
                handleStockpileDoubleClick={handleStockpileDoubleClick}
                handleStockpileHover={(item: string) =>
                  setHoveredStockpile(item)
                }
                hoveredStockpile={hoveredStockpile}
              />
            </HeadRow>
          </TableHead>
          <TableBody className={state.hideMatchingTable && "hidden"}>
            {rows.map((row: any) => (
              <TableRow
                key={row.feature}
                className={prepareRowStyles(row, state, hoveredParam, false)}
              >
                <StyledTableCell
                  align="center"
                  className={classes.dataCell}
                  onClick={() =>
                    higlightItem(
                      row.feature.toUpperCase(),
                      SET_HIGHLIGHTED_ROWS,
                      "highlightedRows",
                      APP_INSIGHTS_EVENTS.CHANGE_ROW_HIGHLIGHT
                    )
                  }
                  onMouseEnter={() =>
                    setHoveredParam(row.feature.toUpperCase())
                  }
                  onMouseLeave={() => setHoveredParam("")}
                >
                  {row.feature}
                </StyledTableCell>
                <DataCell
                  feature={row.feature}
                  stockpiles={row.stockpiles}
                  hoveredStockpile={hoveredStockpile}
                  handleStockpileHover={(item: string) =>
                    setHoveredStockpile(item)
                  }
                  handleStockpileDoubleClick={handleStockpileDoubleClick}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </React.Fragment>
  );
};

export default FilledTable;
